import { Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { SnackbarService } from '../../../shared/services/snackbar/snackbar.service';
import { CommonModule } from '@angular/common';
import { MaintenanceBranchComponent } from './maintenance-branch/maintenance-branch.component';
import { PersonalDetailsComponent } from '../../agent-onboarding/new-rep-application/new-rep-application-section/personal-details/personal-details.component';
import { SpouseDetailsComponent } from '../../agent-onboarding/new-rep-application/new-rep-application-section/spouse-details/spouse-details.component';
import { ContactDetailsComponent } from '../../agent-onboarding/new-rep-application/new-rep-application-section/contact-details/contact-details.component';
import { RegulatoryProfessionalQualificationsComponent } from '../../agent-onboarding/new-rep-application/new-rep-application-section/regulatory-professional-qualifications/regulatory-professional-qualifications.component';
import { BusinessInterestsComponent } from '../../agent-onboarding/new-rep-application/new-rep-application-section/business-interests/business-interests.component';
import { GeneralInformationComponent } from '../../agent-onboarding/new-rep-application/new-rep-application-section/general-information/general-information.component';
import { FitPropercriteriaComponent } from '../../agent-onboarding/new-rep-application/new-rep-application-section/fit-propercriteria/fit-propercriteria.component';
import { MaintenanceAttachmentsComponent } from './maintenance-attachments/maintenance-attachments.component';
import { MaintenanceAllAttachmentsComponent } from './maintenance-all-attachments/maintenance-all-attachments.component';
import { MaintenanceInsuranceProvidersComponent, INSURANCE_PROVIDERS } from './maintenance-insurance-providers/maintenance-insurance-providers.component';
import { MaintenanceOffboardingComponent } from './maintenance-offboarding/maintenance-offboarding.component';
import { PiasLayoutComponent } from '../../../shared/components/pias-layout/pias-layout.component';
import { AdvisorSummary, MaintenanceSection, SectionTheme } from '../maintenance-dashboard/maintenance-dashboard-interfaces/maintenance-dashboard.interface';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MaintenanceAllAttachmentsService } from './maintenance-all-attachments/maintenance-all-attachments-service/maintenance-all-attachments.service';
import { DocumentPreviewOverlayDirective } from '../../../shared/directives/document-preview-overlay/document-preview-overlay.directive';
import { NewRepApplicationService } from '../../agent-onboarding/new-rep-application/new-rep-application-services/new-rep-application.service';
import { PersonalDetailService } from '../../agent-onboarding/new-rep-application/new-rep-application-section/personal-details/personal-details-sercive/personal-detail-service';
import { MatDialog } from '@angular/material/dialog';
import { viewAttachements } from '../../agent-onboarding/new-rep-application/new-rep-application-interfaces/attachements.interface';
import { SessionStorageService } from '../../../core/services/session-storage/session-storage.service';
import { ConfirmPopupComponent } from '../../agent-onboarding/new-rep-application/confirm-popup/confirm-popup.component';
import { HttpStatusCode } from 'axios';
import { MatError } from '@angular/material/form-field';

@Component({
  selector: 'app-maintenace-view',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIcon,
    MatButtonModule,
    PiasLayoutComponent,
    MaintenanceBranchComponent,
    PersonalDetailsComponent,
    SpouseDetailsComponent,
    ContactDetailsComponent,
    RegulatoryProfessionalQualificationsComponent,
    BusinessInterestsComponent,
    GeneralInformationComponent,
    FitPropercriteriaComponent,
    MaintenanceAttachmentsComponent,
    MaintenanceAllAttachmentsComponent,
    MaintenanceInsuranceProvidersComponent,
    MaintenanceOffboardingComponent,
    DocumentPreviewOverlayDirective,
    MatError
  ],
  templateUrl: './maintenace-view.component.html',
  styleUrl: './maintenace-view.component.scss'
})
export class MaintenaceViewComponent {
  maintenanceForm!: FormGroup;
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private snackBarService = inject(SnackbarService);
  private sanitizer = inject(DomSanitizer);
  private maintenanceAllAttachmentsService = inject(MaintenanceAllAttachmentsService);
  private uploadDocuments = inject(NewRepApplicationService);
  private personalDetailService = inject(PersonalDetailService);
  private readonly matDialog = inject(MatDialog);
  private sessionStorageService = inject(SessionStorageService);
  viewattachments!: viewAttachements[];
  personalSupportingDocuments = signal<viewAttachements[]>([]);
  fileError: string = '';
  setSprinner = signal(false);
  data = history.state;
  isReadOnly = signal(false);

  selectedDocument!: SafeResourceUrl;
  isPdf = false;

  readonly MIN_ZOOM = 0.5;
  readonly MAX_ZOOM = 3;
  private readonly ZOOM_STEP = 0.25;
  zoomLevel = 1;
  showPreview = false;
  currentFileName!: string;

  /** The single source of truth for the picker cards and the editor switch. */
  readonly sections: MaintenanceSection[] = [
    { id: 'branchDetails', title: 'Branch Management', icon: 'business', tag: 'ORG', description: 'Branch, financial service director and manager assignment.' },
    { id: 'personalDetails', title: 'Personal Particulars Management', icon: 'person', tag: 'IDENTITY', description: 'Personal, spouse and contact particulars.' },
    { id: 'regulatoryProfessionalQualifications', title: 'Licensing & Financial Advisory Activities', icon: 'workspace_premium', tag: 'LICENSES', description: 'Regulatory modules and professional credentials.' },
    { id: 'insuranceProviders', title: 'Insurance Providers', icon: 'shield', tag: 'PROVIDERS', description: 'Provider firm codes and the user codes allocated to this advisor.' },
    { id: 'businessInterests', title: 'Business Interests & Shareholdings', icon: 'trending_up', tag: 'INTERESTS', description: 'Business interests, shareholdings and ownership.' },
    { id: 'generalInformation', title: 'General Information', icon: 'info', tag: 'DISCLOSURE', description: 'Disclosures and general declarations.' },
    { id: 'fitandProperCriteria', title: 'Fit and Proper Criteria', icon: 'verified_user', tag: 'ASSESSMENT', description: 'Fit-and-proper assessment responses.' },
    { id: 'additionalAttachments', title: 'Additional Documents', icon: 'attach_file', tag: 'DOCUMENTS', description: 'Letter of undertaking and securities interest register.' },
    { id: 'allAttachments', title: 'Uploaded Documents', icon: 'folder_copy', tag: 'DOCUMENTS', description: 'Every document attached to this adviser, with upload date and size.' },
    { id: 'offboarding', title: 'Off-boarding', icon: 'logout', tag: 'CESSATION', description: 'Cessation type, key dates, notice handling and clearance documents.' },
  ];

  /** Section-accent palette (design 1a) — cycled across the cards by index. */
  readonly sectionPalette: SectionTheme[] = [
    { c1: '#1b5aa8', c2: '#3d8bf0', soft: '#bcd6f5', tint: '#eef4fc', glow: 'rgba(27,90,168,.28)' },
    { c1: '#6d3bd4', c2: '#9a6bf0', soft: '#d4c4f5', tint: '#f2ecfd', glow: 'rgba(109,59,212,.26)' },
    { c1: '#c2367f', c2: '#f06aad', soft: '#f3c3dd', tint: '#fdeef6', glow: 'rgba(194,54,127,.24)' },
    { c1: '#0f8a8a', c2: '#25c0be', soft: '#b8e8e6', tint: '#e8faf9', glow: 'rgba(15,138,138,.24)' },
    { c1: '#128a54', c2: '#2fc07f', soft: '#b7e8cf', tint: '#e9faf1', glow: 'rgba(18,138,84,.24)' },
    { c1: '#c07816', c2: '#f0a53d', soft: '#f3dbb0', tint: '#fdf3e3', glow: 'rgba(192,120,22,.24)' }
  ];

  themeFor(index: number): SectionTheme {
    return this.sectionPalette[index % this.sectionPalette.length];
  }

  /** The advisor whose record is being maintained (from the dashboard navigation state). */
  readonly advisor = signal<AdvisorSummary>({
    representativeName: '—',
    preferredName: '—',
    branch: '—',
    representativeCode: '—',
    financialServicesDirector: '—',
    financialServicesManager: '—',
    status: '—'
  });


  readonly statusLabel = computed(() => {
    switch (this.advisorStatus()) {
      case 'Inforced': return 'INFORCE';
      case 'Serving Notice': return 'SERVING NOTICE';
      case 'Resigned': return 'RESIGNED';
      default: return '';
    }
  });

  readonly statusClass = computed(() => {
    switch (this.advisorStatus()) {
      case 'Inforced': return 'status-active';
      case 'Serving Notice': return 'status-notice';
      case 'Resigned': return 'status-resigned';
      default: return 'status-default';
    }
  });

  readonly statusIcon = computed(() => {
    switch (this.advisorStatus()) {
      case 'Inforced': return 'work';
      case 'Serving Notice': return 'pending_actions';
      case 'Resigned': return 'person_remove';
      default: return 'help_outline';
    }
  });

  private readonly advisorStatus = computed(() => (this.advisor().status || '').trim());

  /** `null` = show the picker grid; otherwise the id of the section being edited. */
  activeSectionId = signal<string | null>(null);

  /** The resolved metadata for the section currently being edited. */
  activeSection = computed(() =>
    this.sections.find(s => s.id === this.activeSectionId()) ?? null
  );

  constructor() { }

  ngOnInit() {
    this.buildForm();
    this.isReadOnly.set(this.data.isReadOnly);
    this.loadAdvisor();
    this.viewDocumants();
  }

  /** Reads the selected advisor passed via router navigation state from the dashboard. */
  private loadAdvisor() {
    const applicant = (history.state?.applicant ?? {}) as Record<string, unknown>;
    const value = (key: string) => {
      const raw = applicant[key];
      return typeof raw === 'string' && raw.trim() ? raw : '';
    };
    this.advisor.set({
      representativeName: value('repname'),
      preferredName: value('preferredname'),
      branch: value('branch'),
      representativeCode: value('repcode'),
      // ⚠️ Assumed API keys — cross-check against the backend. `fsd` / `fsm` match
      // the control names Branch Management already uses.
      financialServicesDirector: value('director'),
      financialServicesManager: value('manager'),
      status: value('repstatus')
    });

    const sectionString = sessionStorage.getItem('viewSelectedid');
    const selectedsectionid = sectionString ? JSON.parse(sectionString) : null;

    if (selectedsectionid) {
      this.activeSectionId.set(selectedsectionid.sectionid);
    }
  }

  buildForm() {
    // Each section carries its own `*SupportingDocument` control, following the
    // pattern the Bank section already used.
    this.maintenanceForm = this.fb.group({
      branchDetails: this.fb.group({ branchSupportingDocument: [null] }),
      personalDetails: this.fb.group({ personalSupportingDocument: [null] }),
      spouseDetails: this.fb.group({}),
      contactDetails: this.fb.group({}),
      regulatoryProfessionalQualifications: this.fb.group({}),
      businessInterests: this.fb.group({}),
      generalInformation: this.fb.group({}),
      fitandProperCriteria: this.fb.group({}),
      additionalAttachments: this.fb.group({}),
      offboarding: this.fb.group({}),
      // One { userCode } group per provider, aligned by index to INSURANCE_PROVIDERS.
      // MaintenanceInsuranceProvidersComponent tops the array up if it is short.
      insuranceProviders: this.fb.array(
        INSURANCE_PROVIDERS.map(() => this.fb.group({ userCode: [''] }))
      )
    });
  }

  // ── Section selection / navigation ─────────────────────────
  /** Return to the agent maintenance dashboard. */
  goToDashboard() {
    this.router.navigate(['agent-maintenance']);
    const selectedid = {
      sectionid: null
    };
    sessionStorage.setItem('viewSelectedid', JSON.stringify(selectedid));
  }

  openSection(id: string) {
    this.activeSectionId.set(id);
    const selectedid = {
      sectionid: id
    };
    sessionStorage.setItem('viewSelectedid', JSON.stringify(selectedid));
  }

  backToSections() {
    this.activeSectionId.set(null);
    const selectedid = {
      sectionid: null
    };
    sessionStorage.setItem('viewSelectedid', JSON.stringify(selectedid));
  }

  /** Expand/collapse state for the sub-sections inside Personal Particulars. */
  readonly openSubs = signal<{ personal: boolean; spouse: boolean; contact: boolean }>({
    personal: false,
    spouse: false,
    contact: false
  });

  toggleSub(key: 'personal' | 'spouse' | 'contact'): void {
    this.openSubs.update(map => ({ ...map, [key]: !map[key] }));
  }

  // ── Active-section actions ─────────────────────────────────
  saveSection(action: string) {
    if (action === 'saved') {
      const section = this.activeSection();
      if (!section) {
        return;
      }
      let group: FormGroup | null = null;

      if (this.openSubs().personal) {
        group = this.maintenanceForm.get('personalDetails') as FormGroup;
      }
      else if (this.openSubs().contact) {
        group = this.maintenanceForm.get('contactDetails') as FormGroup;
      }
      else if (this.openSubs().spouse) {
        group = this.maintenanceForm.get('spouseDetails') as FormGroup;
      }
      else {
        group = this.maintenanceForm.get(section.id) as FormGroup;
      }
      if (group && group.invalid) {
        group.markAllAsTouched();
        this.snackBarService.openWarningSnackBar('Please resolve the highlighted fields before saving.');
        return;
      }

      this.snackBarService.openSuccessSnackBar(`${section.title} saved successfully!`);

      if (this.openSubs().personal) {
        this.toggleSub('personal');
        this.toggleSub('spouse');
        this.snackBarService.openSuccessSnackBar('Personal Particulars saved successfully!');
        return;
      }
      if (this.openSubs().spouse) {
        this.toggleSub('spouse');
        this.toggleSub('contact');
        this.snackBarService.openSuccessSnackBar('Spouse Details saved successfully!');
        return;
      }
      if (this.openSubs().contact) {
        this.toggleSub('contact');
        this.backToSections(); return;
      }
      this.backToSections();
    } else {

      if (action === 'cancelled') {
        this.backToSections();
        return;
      }
    }
  }

  cancelSection() {
    this.backToSections();
  }

  /**
   * ⚠️ SAMPLE ONLY — renders a one-page stand-in so the preview panel has something
   * to show for the sample rows above. Delete it with that array; real rows carry
   * whatever URL the document endpoint serves (see `previewdocuments(id)` in the
   * onboarding service for the existing blob-fetch pattern). Files attached in this
   * session are previewed from the browser and need none of this.
   */
  private samplePreviewUrl(title: string): string {
    const label = title.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const lines = [0, 1, 2, 3, 4, 5, 6, 7]
      .map((i) => `<rect x="40" y="${220 + i * 28}" width="${515 - (i % 3) * 90}" height="10" rx="5" fill="#e2e8f0"/>`)
      .join('');

    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="595" height="842" viewBox="0 0 595 842">` +
      `<rect width="595" height="842" fill="#ffffff"/>` +
      `<rect width="595" height="88" fill="#00447f"/>` +
      `<text x="40" y="54" font-family="Open Sans, Arial, sans-serif" font-size="22" fill="#ffffff">PIAS — Sample Document</text>` +
      `<text x="40" y="152" font-family="Open Sans, Arial, sans-serif" font-size="18" font-weight="600" fill="#1e293b">${label}</text>` +
      `<text x="40" y="182" font-family="Open Sans, Arial, sans-serif" font-size="13" fill="#64748b">Placeholder content — the real file comes from the document API.</text>` +
      lines +
      `</svg>`;

    return URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
  }

  // ── Inline file-upload helpers (e.g. bank supporting document) ─────
  onFileSelected(event: Event, group: FormGroup, controlName: string): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length ? input.files[0] : null;
    group.get(controlName)?.setValue(file);
  }

  clearFile(group: FormGroup, controlName: string, input: HTMLInputElement): void {
    group.get(controlName)?.setValue(null);
    input.value = '';
  }

  fileNameOf(group: FormGroup, controlName: string): string {
    const file = group.get(controlName)?.value as File | null;
    return file ? file.name : '';
  }
  //MULTIPLE FILE
  async onMultipleFileSelected(event: Event, group: FormGroup, controlName: string): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    const control = group.get(controlName);
    if (!control) {
      return;
    }
    // Clear previous error
    this.fileError = '';

    const allowedTypes = new Set([
      'application/pdf',
      'image/jpeg',
      'image/png'
    ]);
    const maxSize = 5 * 1024 * 1024; // 5 MB
    if (!allowedTypes.has(file.type)) {
      this.fileError = 'Only PDF, JPG, PNG files are allowed.';
      input.value = '';
      return;
    }
    if (file.size > maxSize) {
      this.fileError = 'File size must be less than or equal to 5MB.';
      input.value = '';
      return;
    }
    // File is valid → continue with your existing functionality
    const files = (control.value as File[]) || [];
    const index = files.length;

    control.setValue([...files, file]);

    control.markAsDirty();
    control.updateValueAndValidity();

    // Clear input so same file can be selected again
    input.value = '';
    // Upload ONLY valid file
    await this.uploadPersonalSupportingDocument(index);
  }

  removeMultipleFile(group: FormGroup, controlName: string, index: number): void {
    const control = group.get(controlName);
    if (!control) {
      return;
    }
    const existingFiles = (control.value as File[]) || [];
    const updatedFiles = existingFiles.filter((_, fileIndex) => fileIndex !== index);
    control.setValue(updatedFiles);
  }

  multipleFilesOf(group: FormGroup, controlName: string): File[] {
    return (group.get(controlName)?.value as File[]) || [];
  }
  validateMultipleFiles(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const files = (control.value as File[]) || [];

      const allowedTypes = new Set([
        'application/pdf',
        'image/jpeg',
        'image/png'
      ]);
      const maxSize = 5 * 1024 * 1024; // 5MB
      for (const file of files) {
        if (!allowedTypes.has(file.type)) {
          return { invalidType: true };
        }
        if (file.size > 5 * 1024 * 1024) {
          return { maxSize: true };
        }
      }
      return null;
    };
  }
  async uploadPersonalSupportingDocument(index: number): Promise<void> {
    const files = this.personalDetailsForm.get('personalSupportingDocument')?.value as File[] || [];
    const file = files[index];
    if (!file) {
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('Description', 'Personal Particulars Supporting Document');
    formData.append('filename', file.name);
    formData.append('ResponseValue', index.toString());
    formData.append('UserId', String(this.data.applicant.onboardingid));

    const responseData = await this.uploadDocuments
      .uploadDocuments(formData)
      .then(response => JSON.parse(response))
      .catch(error => error);
    if (responseData?.status === HttpStatusCode.Ok) {
      this.snackBarService.openSuccessSnackBar('Document uploaded successfully!');
      await this.viewDocumants();
    } else {
      this.snackBarService.openFailureSnackBar('We couldn’t process your request. Please try again');
    }
  }

  async viewDocumants() {
    const repCode = this.data.applicant.repcode;

    const responseData = await this.personalDetailService
      .getPersonalDetails(repCode)
      .then(response => JSON.parse(response))
      .catch(error => error);
    if (!responseData?.data?.responsedata) {
      this.viewattachments = [];
      // this.personalSupportingDocuments.set([]);
      return;
    }
    const documents = responseData.data.responsedata.documents || [];

    // Only Personal Particulars documents
    const personalDocuments = documents.filter((document: viewAttachements) => document.onboardingDocumentType === 'Personal Particulars Supporting Document');
    this.viewattachments = documents;


    this.personalSupportingDocuments.set(personalDocuments);
  }

  async removeFile(document: viewAttachements): Promise<void> {
    const dialogRef = this.matDialog.open(ConfirmPopupComponent,
      {
        width: '500px',
        disableClose: true,
        data: {
          message: 'Do you want to remove the attachement?',
          hidecancel: false
        }
      }
    );

    dialogRef.afterClosed().subscribe(async result => {

      if (result?.data !== true) {
        return;
      }

      const data = {
        onboardingId: this.data.applicant.onboardingid,
        docTypeId: document.docTypeId,
        id: document.id,

      };

      const responseData = await this.uploadDocuments
        .deletedocument(data)
        .then(response => JSON.parse(response))
        .catch(error => error);

      // Check delete API response
      if (
        responseData?.status === HttpStatusCode.Ok ||
        responseData?.data?.status === HttpStatusCode.Ok ||
        responseData?.message?.toLowerCase()?.includes('success')
      ) {

        // Remove ONLY the deleted document from screen
        this.personalSupportingDocuments.update(documents => documents.filter(item => Number(item.id) !== Number(document.id)));
        // Also remove it from viewattachments
        this.viewattachments =
          this.viewattachments?.filter(item => Number(item.id) !== Number(document.id));
        this.snackBarService.openSuccessSnackBar('Document deleted successfully!');
      } else {
        this.snackBarService.openFailureSnackBar('Unable to delete document. Please try again.'
        );
      }

    });
  }

  async previewAttachment(fileName: string[]) {
    this.showPreview = true;
    const id = Number(fileName[0]);
    try {

      const responseData = await this.maintenanceAllAttachmentsService
        .previewdocuments(id);

      // Create blob URL
      const fileURL = URL.createObjectURL(responseData);

      // Safe URL
      this.selectedDocument = this.sanitizer.bypassSecurityTrustResourceUrl(fileURL);
      this.isPdf = fileName[1].toLowerCase().endsWith('.pdf');
      this.currentFileName = fileName[1];

    } catch (error) {
      console.error(error);
    }
    this.resetZoom();
  }


  zoomIn(): void {
    this.zoomLevel = Math.min(this.MAX_ZOOM, +(this.zoomLevel + this.ZOOM_STEP).toFixed(2));
  }

  zoomOut(): void {
    this.zoomLevel = Math.max(this.MIN_ZOOM, +(this.zoomLevel - this.ZOOM_STEP).toFixed(2));
  }

  resetZoom(): void {
    this.zoomLevel = 1;
  }

  closePreview() {
    this.showPreview = false;
    this.resetZoom();
  }

  // ── Form-group accessors ───────────────────────────────────
  get branchDetailsForm(): FormGroup {
    return this.maintenanceForm.get('branchDetails') as FormGroup;
  }
  get personalDetailsForm(): FormGroup {
    return this.maintenanceForm.get('personalDetails') as FormGroup;
  }
  get spouseDetailsForm(): FormGroup {
    return this.maintenanceForm.get('spouseDetails') as FormGroup;
  }
  get contactDetailsForm(): FormGroup {
    return this.maintenanceForm.get('contactDetails') as FormGroup;
  }
  get regulatoryProfessionalQualifications(): FormGroup {
    return this.maintenanceForm.get('regulatoryProfessionalQualifications') as FormGroup;
  }
  get businessInterests(): FormGroup {
    return this.maintenanceForm.get('businessInterests') as FormGroup;
  }
  get generalInformation(): FormGroup {
    return this.maintenanceForm.get('generalInformation') as FormGroup;
  }
  get fitandProperCriteria(): FormGroup {
    return this.maintenanceForm.get('fitandProperCriteria') as FormGroup;
  }
  get additionalAttachments(): FormGroup {
    return this.maintenanceForm.get('additionalAttachments') as FormGroup;
  }
  get offboarding(): FormGroup {
    return this.maintenanceForm.get('offboarding') as FormGroup;
  }
  get insuranceProvidersArray(): FormArray {
    return this.maintenanceForm.get('insuranceProviders') as FormArray;
  }
}
